import SettingsIcon from "@mui/icons-material/Settings";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { SLIDING_APPEARANCE_LIGHT } from "../lib/appearance";
import { SETTINGS_PATH, SLIDING_PUZZLE_MASTER_TITLE } from "../routes";
import {
    PAGE_HERO_CHARCOAL,
    pageHeroDecorLeftSx,
    pageHeroDecorRightSx,
    pageHeroInnerSx,
    pageHeroSectionSx,
    pageHeroTitleSx,
} from "../styles/homeHeroStyles";
import { getHomePremium, homePremiumHeroSx } from "../styles/homePremiumStyles";
import { getSlidingSafeAreaTop, slidingPageColumnSx } from "../styles/mainStyles";

/**
 * Hero header for Home (premium studio), Library, My Puzzles, Settings.
 * @param {"home"|"library"|"myPuzzles"|"settings"} variant
 */
export default function PageHeroSection({
    variant = "home",
    title,
    showSettings = false,
    leading = null,
    children = null,
}) {
    const navigate = useNavigate();
    const { mode, isDark } = useSlidingAppearance();
    const isLight = mode === SLIDING_APPEARANCE_LIGHT;
    const home = getHomePremium(isDark);
    const heading = (title ?? SLIDING_PUZZLE_MASTER_TITLE).toUpperCase();
    const titleRow = variant !== "home" || showSettings;
    const safeTop = getSlidingSafeAreaTop();

    if (variant === "home") {
        return (
            <Box
                component="section"
                aria-label={heading}
                sx={{
                    ...homePremiumHeroSx(),
                    pt: `calc(${safeTop} + 0.35rem)`,
                }}
            >
                <Box
                    sx={{
                        ...slidingPageColumnSx,
                        position: "relative",
                        zIndex: 1,
                        width: "100%",
                        maxWidth: "100%",
                        px: 2,
                        boxSizing: "border-box",
                    }}
                >
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontFamily:
                                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                            fontWeight: 700,
                            textAlign: "center",
                            letterSpacing: "0.1em",
                            fontSize: "0.8125rem",
                            textTransform: "uppercase",
                            color: home.text,
                            mb: children ? 1.25 : 0,
                        }}
                    >
                        {heading}
                    </Typography>
                    {children}
                </Box>
            </Box>
        );
    }

    if (!isLight) {
        return (
            <Box
                component="section"
                aria-label={heading}
                sx={{
                    mb: 1.25,
                    pt: `calc(${safeTop} + 0.35rem)`,
                    px: 0,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        ...slidingPageColumnSx,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: titleRow ? "space-between" : "center",
                        gap: 1,
                        mb: children ? 1 : 0,
                        minHeight: 44,
                    }}
                >
                    {leading ? (
                        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", ml: -1 }}>
                            {leading}
                        </Box>
                    ) : null}
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontFamily:
                                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                            fontWeight: 700,
                            textAlign: "left",
                            letterSpacing: "0.04em",
                            fontSize: "0.9375rem",
                            textTransform: "uppercase",
                            color: home.text,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {heading}
                    </Typography>
                    {showSettings ? (
                        <IconButton
                            type="button"
                            aria-label="Settings"
                            onClick={() => navigate(SETTINGS_PATH)}
                            sx={{ color: home.text, minWidth: 44, minHeight: 44, flexShrink: 0 }}
                        >
                            <SettingsIcon />
                        </IconButton>
                    ) : null}
                </Box>
                {children ? (
                    <Box sx={slidingPageColumnSx}>
                        {children}
                    </Box>
                ) : null}
            </Box>
        );
    }

    return (
        <Box component="section" aria-label={heading} sx={pageHeroSectionSx(variant)}>
            <Box aria-hidden sx={pageHeroDecorLeftSx} />
            <Box aria-hidden sx={pageHeroDecorRightSx} />
            <Box sx={pageHeroInnerSx}>
                {titleRow ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            minHeight: 44,
                            mb: children ? 1.25 : 0,
                        }}
                    >
                        {leading ? (
                            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", ml: -1 }}>
                                {leading}
                            </Box>
                        ) : null}
                        <Typography variant="h5" component="h1" sx={pageHeroTitleSx(variant)}>
                            {heading}
                        </Typography>
                        {showSettings ? (
                            <IconButton
                                type="button"
                                aria-label="Settings"
                                onClick={() => navigate(SETTINGS_PATH)}
                                sx={{
                                    color: PAGE_HERO_CHARCOAL,
                                    minWidth: 44,
                                    minHeight: 44,
                                    flexShrink: 0,
                                }}
                            >
                                <SettingsIcon />
                            </IconButton>
                        ) : null}
                    </Box>
                ) : (
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{ ...pageHeroTitleSx(variant), mb: children ? 1.35 : 0, textAlign: "center" }}
                    >
                        {heading}
                    </Typography>
                )}
                {children}
            </Box>
        </Box>
    );
}
